import type {
  ActionFunctionArgs as RouterActionFunctionArgs,
  LoaderFunctionArgs as RouterLoaderFunctionArgs,
} from 'react-router';
import {
  createContext,
  isRouteErrorResponse,
  matchPath,
  redirect,
  RouterContextProvider,
  useActionData,
  useLoaderData,
  useMatch,
  useNavigation,
  useRouteError as useRouterRouteError,
} from 'react-router';
import { AppStore } from '~/stores/app';
import { Promisable } from './types';

export { redirect };

export const useIsLoading = () => {
  const navigation = useNavigation();

  return navigation.state === 'submitting' || navigation.state === 'loading';
};

// ? normalize as error or response error???
export const useRouteError = () => {
  const error = useRouterRouteError();
  if (error instanceof Error) {
    return error;
  }

  if (isRouteErrorResponse(error)) {
    return new Error('Response Error', { cause: { originalError: error } });
  }

  return new Error('Unknown error occurred', {
    cause: { originalError: error },
  });
};

const shippingProtectionPath = '/:store?/reorder/*';
/**
 * Checks if the current request URL matches the shipping protection path.
 * This is useful for determining if the request is for a shipping protection-related route.
 * This function uses React Router's `matchPath` to check the path against the shipping protection path.
 *
 * @param request - The Request object to check against the shipping protection path.
 * @returns React Router's matchPath result for the shipping protection path.
 */
export const matchShippingProtectionPath = (request: Request) =>
  matchPath(
    {
      path: shippingProtectionPath,
    },
    new URL(request.url).pathname,
  );

export const useShippingProtectionPath = () => useMatch(shippingProtectionPath);

export const appStoreContext = createContext<AppStore>();

type Data = NonNullable<unknown>;
type DataFnValue<T extends Data> = T | Response; // this differs from `DataFunctionValue` of `react-router`, as we omit `null`
type DataFnReturnValue<T extends Data> = Promisable<DataFnValue<T>>;

type LoaderFn<T extends Data> = (
  args: RouterLoaderFunctionArgs<RouterContextProvider>,
  appStore: AppStore,
) => DataFnReturnValue<T>;

type ActionOkResult = { ok: true };
type ActionErrorResult = {
  ok: false;
  message?: string;
};
export type ActionResult = ActionOkResult | ActionErrorResult;

type ActionFn<T extends ActionResult = ActionResult> = (
  args: RouterActionFunctionArgs<RouterContextProvider> & {
    formData: FormData;
  },
  appStore: AppStore,
) => DataFnReturnValue<T>;

//
// Basic loader and action functions for React Router
//

/**
 * Creates a loader function that can be used with React Router with access to the AppStore RouterContext.
 * It provides a `useLoaderData` hook to access the data returned by the loader.
 * If the loader function returns a `Response`, it will be returned as is.
 * @param fn - The loader function to be used with React Router.
 * The function should accept an object with `request` and `params` properties, and the app store as the second argument.
 * The function should return a value that is compatible with `DataFunctionValue` from `react-router`.
 * The function can also return an `Error` or a `Response`.
 * @returns An object with a `useLoaderData` hook to access the data returned by the loader,
 * and a `loader` function to be used with React Router.
 */
export const makeLoader = <T extends Data>(fn: LoaderFn<T>) => ({
  useLoaderData: () => useLoaderData() as T,
  loader: async (args: RouterLoaderFunctionArgs<RouterContextProvider>) =>
    fn(args, args.context.get(appStoreContext)),
});

/**
 * Creates an action function that can be used with React Router.
 * It provides a `useActionResult` hook to access the `ActionResult`.
 * If the action function returns an `Error`, it will be thrown and handled by React Router.
 * @param fn - The action function to be used with React Router.
 * The function should accept an object with `request`, `params`, and `formData` properties, and the app store as the second argument.
 * The function should return a value that is compatible with `ActionResult`.
 * The function can also return an `Error`.
 * @returns An object with a `useActionResult` hook to access the action result,
 * and an `action` function to be used with React Router.
 */
export const makeAction = <T extends ActionResult>(fn: ActionFn<T>) => ({
  useActionResult: () => useActionData() as T | undefined,
  action: async (args: RouterActionFunctionArgs<RouterContextProvider>) =>
    fn(
      { ...args, formData: await args.request.formData() },
      args.context.get(appStoreContext),
    ),
});
